import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import { createElement } from "react";
import type { FieldUiOptions } from "./types";

import { SectionNodeView } from "./views/section-node-view";

/**
 * RichSection node — full rich text content with headings, lists, and code blocks.
 *
 * Used for complex content fields like business logic, technical logic,
 * and technical sources in Fact/KPI entities.
 */
export const RichSection = Node.create({
  name: "richSection",
  // Must stay a superset of `RICH_SECTION_BLOCK_NODE_TYPES` (@repo/shared): a node
  // the backend may write but this expression rejects fails to parse and takes the
  // document down with it (LAT-938). Guarded by a unit test.
  content:
    "(paragraph | heading | bulletList | orderedList | codeBlock | blockquote | horizontalRule | image | dataCatalogTemplateEntry)+",
  defining: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {} as Record<string, unknown>,
      fieldUiOptions: {} as FieldUiOptions,
    };
  },

  addAttributes() {
    return {
      fieldId: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-field-id") ?? "",
        renderHTML: (attributes) => ({ "data-field-id": attributes.fieldId }),
      },
      label: {
        default: "Content",
        parseHTML: (element) => element.getAttribute("data-label") ?? "Content",
        renderHTML: (attributes) => ({ "data-label": attributes.label }),
      },
      guidance: { default: "", rendered: false },
      chromeless: { default: false, rendered: false },
      ownerId: { default: null, rendered: false },
      _diffStatus: { default: null, rendered: false },
      _diffOldValue: { default: null, rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="rich-section"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "rich-section",
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) =>
      createElement(SectionNodeView, {
        ...props,
        fieldUiOptions: this.options.fieldUiOptions,
      })
    );
  },
});
